import { triggerTextNotification } from "components/TheComponents/textNotification";
import RefreshService from "./refreshService";
import clientTrace from "./clientTraceService";
import { URL_BE } from "types/constants";
import {
  ApiServicePostProps,
  ApiServiceRequestProps,
  DefaultRequest,
  ExceptionHandlingType,
} from "types/apiServiceTypes";

class ApiService extends RefreshService {
  private getRequestDefaultOptions(
    input: string | URL,
    init?: RequestInit | undefined
  ): RequestInit {
    const url = new URL(input, URL_BE);
    return {
      method: "GET",
      ...init,
      credentials: "include",
      headers: {
        ...(url.href.startsWith(URL_BE) && {
          "Content-Type": "application/json",
          ...clientTrace.headers(),
        }),
        ...init?.headers,
      },
    };
  }

  async export(url: string, otp: string, type: "csv" | "xlsx", fileName: string = "report") {
    const res: any = await this.request(`${url}/${type}`, {
      method: "GET",
      headers: {
        "x-otp": otp,
      },
    });
    let localUrl: any = null;

    if (type === "csv") {
      const csvData = await res.text();
      const blob = new Blob([csvData], { type: "text/csv" });
      localUrl = window.URL.createObjectURL(blob);
    }

    if (type === "xlsx") {
      const blob = await res.blob();
      localUrl = window.URL.createObjectURL(new Blob([blob]));
    }

    const link = document.createElement("a");
    link.href = localUrl;
    link.setAttribute("download", `${fileName}.${type}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async form(
    input: string | URL,
    formData: FormData,
    exceptionHandlingType: ExceptionHandlingType = ExceptionHandlingType.MANUAL,
    init?: RequestInit | undefined
  ): Promise<Response | undefined> {
    const url = new URL(input, URL_BE);
    try {
      const initalRequest = () =>
        fetch(url.href, {
          method: init?.method || "POST",
          body: formData,
          credentials: "include",
          headers: {
            ...init?.headers,
          },
        });

      let response = await initalRequest();
      if (response && response.status === 401) {
        response = await this.handleUnauthorizedResponse(initalRequest);
      }
      if (
        response &&
        response.status >= 400 &&
        (exceptionHandlingType === ExceptionHandlingType.AUTOMATIC ||
          exceptionHandlingType === ExceptionHandlingType.MANUAL)
      ) {
        const json = await response.json();
        throw new Error(json?.message ?? "Fetch error", { cause: response });
      }
      return response;
    } catch (error: any) {
      if (exceptionHandlingType === ExceptionHandlingType.AUTOMATIC) {
        triggerTextNotification({
          text: error?.message,
          type: "error",
        });
      } else if (exceptionHandlingType === ExceptionHandlingType.MANUAL) {
        throw error;
      }
      return undefined;
    }
  }

  async request(
    input: string | URL,
    init?: RequestInit | undefined,
    exceptionHandlingType: ExceptionHandlingType = ExceptionHandlingType.MANUAL
  ): Promise<Response | undefined> {
    const url = new URL(input, URL_BE);
    try {
      const initalRequest = () => fetch(url.href, this.getRequestDefaultOptions(input, init));

      let response = await initalRequest();
      if (response && response.status === 401) {
        response = await this.handleUnauthorizedResponse(initalRequest);
      }
      if (
        response &&
        response.status >= 400 &&
        (exceptionHandlingType === ExceptionHandlingType.AUTOMATIC ||
          exceptionHandlingType === ExceptionHandlingType.MANUAL)
      ) {
        const json = await response.json();
        throw new Error(json?.message ?? "Fetch error", { cause: response });
      }
      return response;
    } catch (error: any) {
      if (exceptionHandlingType === ExceptionHandlingType.AUTOMATIC) {
        triggerTextNotification({
          text: error?.message,
          type: "error",
        });
      } else if (exceptionHandlingType === ExceptionHandlingType.MANUAL) {
        throw error;
      }
      return undefined;
    }
  }

  async get<T>(
    url: string | URL,
    { init, exceptionHandlingType }: ApiServiceRequestProps = DefaultRequest
  ): Promise<T> {
    const response = await this.request(url, { ...init, method: "GET" }, exceptionHandlingType);
    const json = await response?.json();
    return json as T;
  }

  async delete<T>(
    url: string | URL,
    body?: any,
    { init, exceptionHandlingType }: ApiServicePostProps = DefaultRequest
  ): Promise<T> {
    const stringBody = JSON.stringify(body);
    const response = await this.request(
      url,
      { ...init, body: stringBody, method: "DELETE" },
      exceptionHandlingType
    );
    const json = await response?.json();
    return json as T;
  }

  async post<T>(
    url: string | URL,
    body?: any,
    { init, exceptionHandlingType }: ApiServicePostProps = DefaultRequest
  ): Promise<T> {
    const stringBody = JSON.stringify(body);

    const response = await this.request(
      url,
      { ...init, body: stringBody, method: "POST" },
      exceptionHandlingType
    );
    const json = await response?.json();
    return json as T;
  }

  async patch<T>(
    url: string | URL,
    body?: any,
    { init, exceptionHandlingType }: ApiServicePostProps = DefaultRequest
  ): Promise<T> {
    const stringBody = JSON.stringify(body);

    const response = await this.request(
      url,
      { ...init, body: stringBody, method: "PATCH" },
      exceptionHandlingType
    );
    const json = await response?.json();
    return json as T;
  }

  async put<T>(
    url: string | URL,
    body?: any,
    { init, exceptionHandlingType }: ApiServicePostProps = DefaultRequest
  ): Promise<T> {
    const stringBody = JSON.stringify(body);
    const response = await this.request(
      url,
      { ...init, body: stringBody, method: "PUT" },
      exceptionHandlingType
    );
    const json = await response?.json();
    return json as T;
  }
}

const apiService = new ApiService();
export default apiService;
export { ApiService };
